/**
 * ImageLayoutModal — the step that runs before the file picker when the user
 * adds photos to a post: pick how the photos should appear, then the composer
 * asks for exactly the number of images that layout needs. Mirrors mobile's
 * imageLayoutSheet (apps/mobile/src/screens/main/createPost/imageLayoutSheet).
 */
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { POST_IMAGE_LAYOUT } from '@wlb/shared/constants';
import type { PostImageLayout } from '@wlb/shared/types/post';
import { Modal } from '../../components/ui/Modal';
import { ChevronRightIcon, StarIcon } from '../../components/ui/icons';
// The artwork mobile's imageLayoutSheet shows, copied into the web bundle:
// Metro `require()` assets cannot be imported from the browser build.
import layoutSingleImage from '../../assets/images/layout-single.png';
import layoutTwoCompareImage from '../../assets/images/layout-two-compare.png';
import layoutFourCompareImage from '../../assets/images/layout-four-compare.png';
import './photoLayout.css';

interface LayoutOption {
  key: PostImageLayout;
  illustration: string;
  titleKey: string;
  descriptionKey: string;
  /** Present on the layout we recommend; renders the badge. */
  badgeKey?: string;
}

/**
 * Offered layouts. The gallery layout is withheld for now — the composer and
 * the feed still render gallery posts, it just cannot be chosen for a new one.
 */
const LAYOUT_OPTIONS: LayoutOption[] = [
  {
    key: POST_IMAGE_LAYOUT.SINGLE,
    illustration: layoutSingleImage,
    titleKey: 'home.createPost.media.singlePhoto',
    descriptionKey: 'home.createPost.media.singlePhotoDesc',
  },
  {
    key: POST_IMAGE_LAYOUT.COMPARISON_2,
    illustration: layoutTwoCompareImage,
    titleKey: 'home.createPost.media.twoCompare',
    descriptionKey: 'home.createPost.media.twoCompareDesc',
    badgeKey: 'home.createPost.media.mostPopular',
  },
  {
    key: POST_IMAGE_LAYOUT.COMPARISON_4,
    illustration: layoutFourCompareImage,
    titleKey: 'home.createPost.media.fourCompare',
    descriptionKey: 'home.createPost.media.fourCompareDesc',
  },
];

interface ImageLayoutModalProps {
  /** Layout the composer is already using, highlighted as the current choice. */
  selectedLayout?: PostImageLayout | null;
  onSelect: (layout: PostImageLayout) => void;
  onClose: () => void;
}

export function ImageLayoutModal({ selectedLayout = null, onSelect, onClose }: ImageLayoutModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      title={t('home.createPost.media.selectLayoutTitle')}
      onClose={onClose}
      labelledBy="image-layout-title"
      className="photo-layout-modal"
    >
      <div className="photo-layout">
        <p className="photo-layout__subtitle">
          {t('home.createPost.media.selectLayoutSubtitle')}
        </p>
        <ul className="photo-layout__options">
          {LAYOUT_OPTIONS.map(({ key, illustration, titleKey, descriptionKey, badgeKey }) => (
            <li key={key}>
              <button
                type="button"
                className={`photo-layout__option${badgeKey ? ' photo-layout__option--recommended' : ''}${selectedLayout === key ? ' photo-layout__option--selected' : ''}`}
                aria-current={selectedLayout === key ? 'true' : undefined}
                onClick={() => onSelect(key)}
              >
                <span className="photo-layout__illustration">
                  <img src={illustration} alt="" aria-hidden="true" />
                </span>
                <span className="photo-layout__text">
                  <span className="photo-layout__title">{t(titleKey)}</span>
                  <span className="photo-layout__description">{t(descriptionKey)}</span>
                </span>
                {badgeKey && (
                  <span className="photo-layout__badge">
                    <StarIcon size={12} aria-hidden />
                    {t(badgeKey)}
                  </span>
                )}
                <ChevronRightIcon size={18} className="photo-layout__chevron" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
